/**
 * Language Preference Utilities
 * Provides isolated helpers for guest language persistence and startup language resolution
 */

export const SUPPORTED_LANGUAGE_CODES = ['en', 'de', 'nl', 'fr'];

const GUEST_LANGUAGE_STORAGE_KEY = 'heatcraft:guest-language';
const LEGACY_LANGUAGE_STORAGE_KEY = 'i18nextLng';
const AUTH_USER_STORAGE_KEY = 'user';

/**
 * Normalize a language code to the app's supported base format.
 *
 * @param {string|null|undefined} languageCode - The raw language code
 * @returns {string|null} Normalized base language code or null when invalid
 */
export const normalizeLanguageCode = (languageCode) => {
  if (typeof languageCode !== 'string') {
    return null;
  }

  const normalizedCode = languageCode.trim().toLowerCase();
  if (!normalizedCode) {
    return null;
  }

  return normalizedCode.split('-')[0];
};

/**
 * Check whether a language code is supported by the application.
 *
 * @param {string|null|undefined} languageCode - The language code to validate
 * @returns {boolean} True when the code matches a supported language
 */
export const isSupportedLanguage = (languageCode) => {
  const normalizedCode = normalizeLanguageCode(languageCode);
  return normalizedCode ? SUPPORTED_LANGUAGE_CODES.includes(normalizedCode) : false;
};

/**
 * Read a string value from web storage safely.
 *
 * @param {Storage|undefined} storage - Web storage instance
 * @param {string} key - Storage key to read
 * @returns {string|null} Stored value or null when unavailable
 */
const readStorageValue = (storage, key) => {
  if (!storage) {
    return null;
  }

  try {
    return storage.getItem(key);
  } catch (error) {
    return null;
  }
};

/**
 * Persist a string value to web storage safely.
 *
 * @param {Storage|undefined} storage - Web storage instance
 * @param {string} key - Storage key to write
 * @param {string} value - Value to persist
 */
const writeStorageValue = (storage, key, value) => {
  if (!storage) {
    return;
  }

  try {
    storage.setItem(key, value);
  } catch (error) {
    // Ignore storage failures and keep runtime language state only.
  }
};

/**
 * Remove a key from web storage safely.
 *
 * @param {Storage|undefined} storage - Web storage instance
 * @param {string} key - Storage key to remove
 */
const removeStorageValue = (storage, key) => {
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(key);
  } catch (error) {
    // Ignore storage failures and keep runtime language state only.
  }
};

/**
 * Get the cached authenticated user's preferred language, if available.
 *
 * @returns {string|null} Preferred language from cached authenticated user data
 */
export const getStoredAuthenticatedLanguage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const storedUser = readStorageValue(window.localStorage, AUTH_USER_STORAGE_KEY);
  if (!storedUser) {
    return null;
  }

  try {
    const parsedUser = JSON.parse(storedUser);
    const preferredLanguage = normalizeLanguageCode(parsedUser?.preferredLanguage);
    return isSupportedLanguage(preferredLanguage) ? preferredLanguage : null;
  } catch (error) {
    return null;
  }
};

/**
 * Get the persisted guest language preference.
 * Falls back to the legacy i18next cache key once and migrates it to the guest key.
 *
 * @returns {string|null} Guest language preference when available
 */
export const getGuestLanguagePreference = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const storedGuestLanguage = normalizeLanguageCode(
    readStorageValue(window.localStorage, GUEST_LANGUAGE_STORAGE_KEY)
  );

  if (isSupportedLanguage(storedGuestLanguage)) {
    return storedGuestLanguage;
  }

  const legacyLanguage = normalizeLanguageCode(
    readStorageValue(window.localStorage, LEGACY_LANGUAGE_STORAGE_KEY)
  );

  if (isSupportedLanguage(legacyLanguage)) {
    writeStorageValue(window.localStorage, GUEST_LANGUAGE_STORAGE_KEY, legacyLanguage);
    return legacyLanguage;
  }

  return null;
};

/**
 * Persist the guest language preference without touching authenticated user state.
 *
 * @param {string} languageCode - Language code selected by an anonymous visitor
 */
export const setGuestLanguagePreference = (languageCode) => {
  if (typeof window === 'undefined') {
    return;
  }

  const normalizedCode = normalizeLanguageCode(languageCode);
  if (!isSupportedLanguage(normalizedCode)) {
    return;
  }

  writeStorageValue(window.localStorage, GUEST_LANGUAGE_STORAGE_KEY, normalizedCode);
  removeStorageValue(window.localStorage, LEGACY_LANGUAGE_STORAGE_KEY);
};

/**
 * Clear persisted guest and legacy language preferences.
 */
export const clearGuestLanguagePreference = () => {
  if (typeof window === 'undefined') {
    return;
  }

  removeStorageValue(window.localStorage, GUEST_LANGUAGE_STORAGE_KEY);
  removeStorageValue(window.localStorage, LEGACY_LANGUAGE_STORAGE_KEY);
};

/**
 * Resolve the best language to use during i18n startup.
 * Authenticated user preference wins over anonymous guest preference.
 *
 * @returns {string|null} Initial language code or null to allow browser detection
 */
export const getInitialLanguagePreference = () => {
  const authenticatedLanguage = getStoredAuthenticatedLanguage();
  if (authenticatedLanguage) {
    return authenticatedLanguage;
  }

  return getGuestLanguagePreference();
};

/**
 * Resolve the browser's preferred supported language.
 *
 * @returns {string} Supported browser language or the application fallback
 */
export const getBrowserLanguagePreference = () => {
  if (typeof window === 'undefined') {
    return 'en';
  }

  const browserLanguages = [
    ...(Array.isArray(window.navigator?.languages) ? window.navigator.languages : []),
    window.navigator?.language,
  ];

  for (const languageCode of browserLanguages) {
    const normalizedCode = normalizeLanguageCode(languageCode);
    if (isSupportedLanguage(normalizedCode)) {
      return normalizedCode;
    }
  }

  return 'en';
};

export default {
  SUPPORTED_LANGUAGE_CODES,
  normalizeLanguageCode,
  isSupportedLanguage,
  getStoredAuthenticatedLanguage,
  getGuestLanguagePreference,
  setGuestLanguagePreference,
  clearGuestLanguagePreference,
  getInitialLanguagePreference,
  getBrowserLanguagePreference,
};